import { useQuery } from '@tanstack/react-query';
import { useRouter, useSearch } from '@tanstack/react-router';
import { FilterSummary, Pill, TableCard, type Column } from '@cwos/ui-admin';
import { useNav } from '../navigation.js';
import { listPhase } from '../listState.js';
import { ListState } from '../ListStates.js';
import { mayReadIntegrationsHealth, usePermissions } from '../permissions.js';
import { useDataHealthCheck } from '../dataHealth.js';
import { insights, type FindingSeverity, type FindingSummary } from './insightsClient.js';
import { ForecastPanel } from './ForecastPanel.js';
import {
  DEFAULT_VIEW,
  VIEWS,
  emptySentence,
  entitySentence,
  frameSentence,
  recurrenceSentence,
  pillVariantFor,
  severityLabel,
  subjectsOf,
  viewFor,
} from './insightsLogic.js';
import { ownerCellLabel } from './ownerLogic.js';

/**
 * INTELLIGENCE → INSIGHTS. What Phibian noticed, and what to do about it.
 *
 * ===========================================================================
 * THIS IS A PRODUCT SURFACE, NOT A DEBUG FEED
 * ===========================================================================
 * A debug feed would show signals, detector ids, confidences and run hashes at
 * the top level, ordered by recency, with a count badge on every tab. This
 * shows a SENTENCE about a condition, ordered by a stored priority, with the
 * machinery available on the detail page for the reader who wants it. The run
 * ids and the signal hash are real and are shown — a person asking "why does
 * it say that" deserves them — but they are the last thing on the page rather
 * than the first.
 *
 * ===========================================================================
 * THE TONE RULE, AND WHERE IT IS ENFORCED
 * ===========================================================================
 * "The UI must encourage investigation, not anxiety. No giant red alert wall.
 * WATCH EVERYTHING. NOTIFY VERY LITTLE."
 *
 * Three things make that true here rather than merely intended:
 *
 *   1. `toneFor` in `insightsLogic.ts` gives info AND warning a quiet
 *      treatment. Only `critical` is emphatic, and the detectors reach
 *      critical only for a condition that has already lapsed — the
 *      expiring-credential detector's band is capped at 'warning' in
 *      `sources.ts`, so "expires in four days" cannot render red however
 *      close it gets.
 *   2. NO COUNT BADGE ON A VIEW TAB. A number beside a tab is a standing
 *      instruction to make it go down, and most of this queue is things to
 *      know rather than things to clear.
 *   3. The default view is **Needs attention**, which is the smallest of the
 *      four by construction: everything a person has looked at moves to
 *      Watching and stops asking.
 *
 * ===========================================================================
 * URL AS TRUTH
 * ===========================================================================
 * The view and every filter live in the query string, so a finding queue is
 * linkable, back/forward work, and a reload lands where the reader was. The
 * detail page is a real route for the same reason a documentation record is:
 * "send me the one about the lapsed credential" has to be a URL.
 */

/** The findings list route caps `limit` at 100 (findings/routes.ts); above it every load is a 400. */
const LIST_PAGE_SIZE = 100;

/** The reading permission this screen's server guard is argued from. */
export const INSIGHTS_READ_KEY = 'insights:read';

/**
 * The sentence `RequirePermission` shows when the key is missing. The key
 * alone does not describe this screen to somebody who has never seen it.
 */
export const INSIGHTS_IS =
  'Insights is where Phibian writes down what it noticed in your own records — a lapsing ' +
  'credential, a supervision gap, drives nobody classified — with the evidence it read and ' +
  'what you might do about it.';

interface InsightsSearch {
  view?: string;
  severity?: string;
  program?: string;
  site?: string;
  owner?: string;
}

export function Insights() {
  const router = useRouter();
  const navigate = useNav();
  const search = useSearch({ strict: false }) as InsightsSearch;
  const view = viewFor(search.view ?? DEFAULT_VIEW);
  /**
   * The same cross-check Needs Attention makes (`dataHealth.ts`), reused here
   * because "nothing is waiting on a first look" is the same species of
   * completeness claim `empty.successAttention` makes: findings come from
   * `domains/rules`, which has never once referenced `domains/integrations`.
   */
  const { has } = usePermissions();
  const dataHealthCheckResult = useDataHealthCheck(mayReadIntegrationsHealth(has));

  const severity = search.severity as FindingSeverity | undefined;
  const programId = search.program;
  const locationId = search.site;
  const ownerUserId = search.owner;

  const list = useQuery({
    queryKey: [
      'insights',
      'findings',
      view.key,
      severity ?? 'all',
      programId ?? 'all',
      locationId ?? 'all',
      ownerUserId ?? 'all',
    ],
    queryFn: () =>
      insights.list({
        state: view.key,
        severity,
        programId,
        locationId,
        ownerUserId,
        limit: LIST_PAGE_SIZE,
      }),
  });

  const items = list.data?.items ?? [];
  // READ OFF THE RESPONSE. The server said which population it answered about;
  // re-deriving it from the caller's permissions is how a narrowed list comes
  // to be labelled organization-wide.
  const organizationWide = list.data?.organizationWide;

  const setSearch = (next: Partial<InsightsSearch>): void => {
    const merged: Record<string, string> = {};
    for (const [key, value] of Object.entries({ ...search, ...next })) {
      if (typeof value === 'string' && value !== '') merged[key] = value;
    }
    void router.navigate({ to: '/insights', search: merged });
  };

  const columns: Column<FindingSummary>[] = [
    {
      key: 'what',
      header: 'What Phibian noticed',
      render: (row) => (
        <div className="rn-col" style={{ gap: 'var(--space-8)' }}>
          <span className="rn-strong">{row.title}</span>
          <span className="rn-muted">
            {subjectsOf(row.entities).map(entitySentence).join(' · ') || 'No subject recorded'}
          </span>
        </div>
      ),
    },
    {
      key: 'severity',
      header: 'Reading',
      render: (row) => (
        <Pill variant={pillVariantFor(row.severity)}>{severityLabel(row.severity)}</Pill>
      ),
    },
    {
      key: 'recurrence',
      header: 'History',
      render: (row) => <span className="rn-muted">{recurrenceSentence(row.recurrenceCount)}</span>,
    },
    {
      key: 'owner',
      header: 'Owner',
      // THREE STATES IN A CELL. `ownerCellLabel` has a branch for each, so
      // "not decided" and "a role owns this, and nobody holds it" are two
      // different cells rather than one blank.
      render: (row) => (
        <span className="rn-muted" data-owner-state={row.owner.kind}>
          {ownerCellLabel(row.owner)}
        </span>
      ),
    },
    {
      key: 'lastSeen',
      header: 'Last seen',
      render: (row) => (
        <time dateTime={row.lastSeenAt}>{new Date(row.lastSeenAt).toLocaleDateString()}</time>
      ),
    },
  ];

  return (
    <div className="rn-col" style={{ gap: 'var(--space-16)' }}>
      <header className="rn-col" style={{ gap: 'var(--space-8)' }}>
        <h1>Insights</h1>
        <p className="rn-muted">{frameSentence(organizationWide)}</p>
        {/*
          THE WAY IN TO THE ESCALATION QUEUE.
          Escalations that resolved to nobody are not findings and do not belong
          in the tabs below — a finding's lifecycle is needs_attention /
          watching / resolved / dismissed, and "no escalation owner configured"
          is none of those. It is a management queue about the ORGANIZATION's
          configuration, so it gets its own destination and this link is the
          click path to it.
        */}
        <a className="rn-link" href="/insights/escalations">
          Escalations waiting on somebody →
        </a>
      </header>

      {/*
        ANTICIPATE, ABOVE THE QUEUE AND OUTSIDE THE TABS.
        Outside, because the four view tabs are a finding's lifecycle
        (needs_attention / watching / resolved / dismissed) and a forecast has
        none of those states — it is pending, or it has been scored. A fifth tab
        would say it belongs to the same machine. Above, because the point of a
        forecast is to be read before the thing it is about becomes a row in the
        queue below.
      */}
      <ForecastPanel />

      <nav className="rn-tabs" aria-label="Insight views">
        {VIEWS.map((entry) => (
          <button
            key={entry.key}
            type="button"
            className="rn-tab"
            aria-pressed={entry.key === view.key}
            onClick={() => setSearch({ view: entry.key })}
          >
            {entry.label}
          </button>
        ))}
      </nav>
      <p className="rn-muted">{view.descriptor}</p>

      <FilterSummary
        scope={{
          what: 'findings',
          scope: organizationWide === false ? 'the people you supervise' : 'your organization',
          period: 'as of the last observation sweep',
          narrowings: [
            { label: view.label.toLowerCase() },
            ...(severity ? [{ label: severityLabel(severity).toLowerCase() }] : []),
            ...(programId ? [{ label: 'one program' }] : []),
            ...(locationId ? [{ label: 'one site' }] : []),
            ...(ownerUserId ? [{ label: 'one owner' }] : []),
          ],
          // The server's own frame, not a guess. `undefined` while the read is
          // in flight is NOT "nothing narrows this": it is not yet known.
          narrowedBy:
            organizationWide === undefined
              ? { kind: 'unknown' as const }
              : organizationWide
                ? undefined
                : 'the people you supervise',
        }}
      />

      <ListState
        phase={listPhase(list, items.length)}
        surface="table"
        rows={6}
        failedMessage="We could not load what Phibian noticed. Nothing has changed."
        onRetry={() => void list.refetch()}
        empty={
          <p className="rn-muted">{emptySentence(view, organizationWide, dataHealthCheckResult)}</p>
        }
      >
        <TableCard
          columns={columns}
          rows={items}
          rowKey={(row) => row.id}
          onRowOpen={(row) => navigate(`/insights/${row.id}`)}
          caption={`Findings — ${view.label}`}
        />
      </ListState>
    </div>
  );
}
